import * as woop from "../../../app/core/woop";
import { ComponentOptions } from "../../components/components/component";
import { Container, Pane } from "../../components/components/index";
import { UserComponent } from "../../components/components/userComponent";
import { bind, observable } from "../../core/kendo";
import { bindLocalizedLanguage } from "../../core/ll";
import { deleteEntity } from "../deleteEntity";
import * as gridUtil from "../gridUtil";
import { Grid, InsertTabGroup } from "./index";
import { ItemSelector } from "./itemSelector";

export interface GridPaneOptions extends ComponentOptions {
    view?: () => any;

    parentPaneId?: string;
    entityType?;
    parentProperty?;
    alwaysSelect?;
    expand?;
    columnOrder?;
    columnRenames?;
    columns?;
    gridHeight?;
    stickyDefaultName?;
    utilityColumns?;
}

export class GridPane extends UserComponent {

    options: GridPaneOptions;
    viewParams: any;
    gridOptions: any;
    total: number;
    itemSelector: any;
    idToSelect: string;
    urlPrefix: string;
    beforeSend: any;

    constructor(options: GridPaneOptions) {
        var view = options.view();

        const gridPaneView = () => new Pane({
            id: "MainPane",
            header: view.header,
            actions: view.actions,
            popUpOnlyActions: view.popUpOnlyActions,
            components: [
                new Container({
                    components: [
                        new Container({
                            type: "Container",
                            id: "InsertTabGroupContainer",
                            classes: "col-xs-12",
                            components: [
                                new Container({
                                    type: "Container",
                                    id: "TopComponentsContainer",
                                    components: view.topComponents
                                }),
                                new InsertTabGroup({
                                    type: "InsertTabGroup",
                                    id: "InsertTabGroup",
                                    components: view.insertTabs
                                })
                            ]
                        }),
                        new Grid({
                            id: "Grid",
                            containerClasses: "col-xs-12 pad-b-15",
                            stickyDefaultName: options.stickyDefaultName
                        }),
                        new Container({
                            id: "DetailsContainer",
                            components: view.details
                        })
                    ]
                })
            ]
        });

        super(gridPaneView, options);

        this.viewParams = {
            entityType: view.entityType,
            entityTypes: view.entityTypes,
            parentProperty: view.parentProperty,
            alwaysSelect: view.alwaysSelect,
            expand: view.expand,
            columnOrder: view.columnOrder,
            columnRenames: view.columnRenames,
            columns: view.columns,
            gridHeight: view.gridHeight,
            utilityColumns: view.utilityColumns
        };
    }

    preRender() {
        super.preRender();

        var view = this.viewParams;
        this.gridOptions = {
            entityType: this.options.entityType || view.entityType,
            stickyDefaultName: this.options.stickyDefaultName,
            parentProperty: this.options.parentProperty || view.parentProperty,
            alwaysSelect: this.options.alwaysSelect || view.alwaysSelect,
            expand: this.options.expand || view.expand,
            columnOrder: this.options.columnOrder || view.columnOrder,
            columnRenames: this.options.columnRenames || view.columnRenames,
            columns: this.options.columns || view.columns || [],
            gridHeight: this.options.gridHeight || view.gridHeight,
            utilityColumns: this.options.utilityColumns || view.utilityColumns
        };
        this.total = 0;
    }

    bindEvents() {
        var me = this;

        $("#" + me.id).addClass("hidden");
        me.get("#DetailsContainer").hide();

        me.setupItemSelector();
        me.bindGridEvents();
        me.bindInsertTabEvents();
        (me as any).bindAdditionalEvents && (me as any).bindAdditionalEvents();

        if (me.options.parentPaneId) {
            var parentElement = me.options.zScreen.get("#" + me.options.parentPaneId);
            if (parentElement) {
                parentElement.data("me").addChildPane(me);
            }
        }

        bindLocalizedLanguage({ container: me.get("#MainPane") });
    }

    setupItemSelector() {
        var me = this;

        me.itemSelector = new ItemSelector({
            whenParentItemChanged: function (parentItem) {
                if (parentItem) {
                    $("#" + me.id).removeClass("hidden");
                    me.refreshGrid();
                } else {
                    $("#" + me.id).addClass("hidden");
                }

                (me as any).whenParentItemChanged && (me as any).whenParentItemChanged(parentItem);
            },
            whenSelectedItemChanged: function (selectedItem) {
                me.bindDetailsContainer();

                // Close any open woop/InsertTab when the selection changes.
                // Specifically, we care that any open woops of child panes are closed, but this closes any woop.
                woop.cancelSelectedWoop();

                (me as any).whenSelectedItemChanged && (me as any).whenSelectedItemChanged(selectedItem);
            }
        });
    }

    bindDetailsContainer() {
        var me = this;

        var selectedItem = me.getSelectedItem();
        bind({ container: me.get("#DetailsContainer"), bind: ".bind-value", unbind: true });
        if (selectedItem) {
            bind({ container: me.get("#DetailsContainer"), bind: ".bind-value", data: selectedItem });
            me.get("#DetailsContainer").show();
        } else {
            me.get("#DetailsContainer").hide();
        }
    }

    bindGridEvents() {
        var me = this;

        var columns = me.gridOptions.columns.slice();

        var grid = me.get("#Grid").data("me") as Grid;
        grid.build({
            id: "Grid",
            zScreen: me.zScreen,
            stickyDefaultName: me.gridOptions.stickyDefaultName,
            entity: me.gridOptions.entityType,
            filter: function () {
                return "{0} eq {1}".format(me.gridOptions.parentProperty, (me.itemSelector.parentItem && me.itemSelector.parentItem.Id) || 0);
            },
            alwaysSelect: me.gridOptions.alwaysSelect,
            expand: me.gridOptions.expand,
            height: me.gridOptions.gridHeight,
            autoBind: false,
            columnOrder: me.gridOptions.columnOrder,
            columnRenames: me.gridOptions.columnRenames,
            columnOverrides: columns,
            utilityColumns: me.gridOptions.utilityColumns,
            editButton: true,
            deleteButton: true,
            change: function (e) {
                var row = grid.kendoGrid.select().closest("tr");
                var item = grid.kendoGrid.dataItem(row);

                me.itemSelector.setSelectedItem(item);
                (me as any).gridChange && (me as any).gridChange({e: e, row: row, item: item, grid: this});
            },
            dataBound: function (e) {
                me.total = grid.kendoGrid.dataSource.total();

                if (me.total == 0) {
                    me.itemSelector.setSelectedItem(null);
                }

                var row = grid.kendoGrid.select().closest("tr");
                var selectedItem = grid.kendoGrid.dataItem(row);

                me.itemSelector.setSelectedItem(selectedItem);

                if (me.idToSelect) {
                    selectedItem = observable({ Id: me.idToSelect });
                    me.idToSelect = null;
                }

                gridUtil.selectPrevious({ observable: selectedItem, grid: grid.kendoGrid, e: e });

                me.bindDetailsContainer();
                (me as any).dataBound && (me as any).dataBound({ e: e, grid: this });
            },
            beforeSend: me.beforeSend,
            urlPrefix: me.urlPrefix
        });

        grid.kendoGrid.table.off("dblclick", "tbody > tr").tie("dblclick", "tbody > tr", function (e) {
            e.preventDefault();
            (me as any).editSelected && (me as any).editSelected();
        });

        grid.kendoGrid.table.off("click", ".k-grid-edit").tie("click", ".k-grid-edit", function (e) {
            e.preventDefault();
            (me as any).editSelected && (me as any).editSelected();
        });

        grid.kendoGrid.table.off("click", ".k-grid-delete").tie("click", ".k-grid-delete", function (e) {
            e.preventDefault();
            me.deleteSelected();
        });
    }

    bindInsertTabEvents() {
        var me = this;

        me.forEachTab(function (index, tab) {
            tab.options.entityType = me.gridOptions.entityType;
            tab.initialize({
                beforeShowInsertContainer: function (obs) {
                    (me as any).beforeShowInsertContainer && (me as any).beforeShowInsertContainer(obs, index, tab);
                },
                setupObservable: function (obs) {
                    obs.set(me.gridOptions.parentProperty, me.itemSelector.parentItem.Id);

                    (me as any).setupObservable && (me as any).setupObservable(obs, index, tab);
                },
                deleteDtoProperties: function (dto) {
                    if (!(me as any).deleteDtoProperties) {
                        return dto;
                    }

                    var returnDto = (me as any).deleteDtoProperties(dto, index, tab);
                    if (returnDto) {
                        return returnDto;
                    }

                    return dto;
                },
                insertedSuccessfully: function (data) {
                    me.idToSelect = data.d.results;
                    var dataSource = me.get("#Grid").data("kendoGrid").dataSource;
                    gridUtil.lastPage(dataSource);
                }
            });
        });
    }

    forEachTab(visitor) {
        var me = this;

        $.each(me.get("#InsertTabGroup").data("me").tabs(), function (index, tab) {
            visitor(index, tab);
        });
    }

    setParentItem(parentItem) {
        var me = this;

        me.itemSelector.setParentItem(parentItem);
    }

    getParentItem() {
        var me = this;

        return me.itemSelector.parentItem;
    }

    getSelectedItem() {
        var me = this;

        return me.itemSelector.selectedItem;
    }

    addChildPane(childPane) {
        var me = this;

        if (childPane.itemSelector) {
            me.itemSelector.addChildSelector(childPane.itemSelector);
        }
    }

    deleteSelected() {
        var me = this;

        deleteEntity({
            id: me.itemSelector.selectedItem.Id,
            entity: me.gridOptions.entityType,
            urlPrefix: me.urlPrefix,
            beforeSend: me.beforeSend,
            success: function () {
                me.refreshGrid();
            }
        });
    }

    refreshGrid() {
        var me = this;

        if (me.gridOptions && me.gridOptions.parentProperty && me.itemSelector && me.itemSelector.parentItem && me.itemSelector.parentItem.Id) {
            me.get("#Grid").refreshGrid();
        }
    }

    setUrlPrefix(urlPrefix) {
        var me = this;

        me.urlPrefix = urlPrefix;
        me.forEachTab(function (index, tab) {
            tab.setUrlPrefix(urlPrefix);
        });
    }

    setBeforeSend(beforeSend) {
        var me = this;

        me.beforeSend = beforeSend;
        me.forEachTab(function (index, tab) {
            tab.setBeforeSend(beforeSend);
        });
    }
}
